import React, { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import CourseCard from '../components/CourseCard';
import { useSelector } from 'react-redux';
import { selectElearningData } from 'store/elearning.slice';
import TimeExceedWarning from '../components/TimeExceedWarning';
import {
  Spinner,
  colors,
  spacing,
  typography,
} from 'design-system';

const MOBILE = '@media (max-width: 768px)';

function ElearningStudentCoursePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const elearningData = useSelector(selectElearningData);
  const {
    elearningCourses,
    // Raw `core_course_get_contents` payload keyed by courseId, populated
    // once by StudentElearningLayout (loadElearningCoursesData) for every
    // student page. Reuse it here — refetching per-course on this mount
    // was a duplicate N+1 against the same Moodle WS (perf test: 6× of
    // /elearning/courses/:id/contents firing in parallel on top of 6× of
    // /elearning/course/content from the Layout's getCoursesContents).
    courseContents,
    isLimitExceeded,
    timeLimitPerDay,
    totalTodayTime,
    elearningUser,
  } = elearningData;
  const courses = useMemo(() => {
    if (!elearningCourses) return [];

    return Object.values(elearningCourses || {}).filter((course) => course?.visible);
  }, [elearningCourses]);

  const handleSelectCourse = (course) => {
    setSearchParams({ courseId: course.id });
  };

  if (isLimitExceeded) {
    return (
      <TimeExceedWarning
        timeLimitPerDay={timeLimitPerDay}
        totalTodayTime={totalTodayTime}
      />
    );
  }

  if (!elearningUser) {
    return (
      <PageWrap>
        <Container className='py-3 py-md-4'>
          <PageTitle>Danh sách môn học</PageTitle>
          <Spinner block label='Đang tải dữ liệu...' />
        </Container>
      </PageWrap>
    );
  }

  if (elearningUser && !elearningUser?.elearningLessons) {
    return (
      <PageWrap>
        <Container className='py-3 py-md-4'>
          <PageTitle>Danh sách môn học</PageTitle>
          <EmptyState>Không có môn học nào</EmptyState>
        </Container>
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <Container className='py-3 py-md-4'>
        <PageTitle>Danh sách môn học</PageTitle>
        {courses.length === 0 && elearningUser?.elearningLessons?.length > 0 ? (
          <Spinner block label='Đang tải dữ liệu...' />
        ) : null}
        {courses.length > 0 && (
          <Row className='g-3'>
            {courses?.map((course) => {
              return (elearningUser?.elearningLessons || []).includes(
                course.id
              ) ? (
                <Col key={course.id} xs={12}>
                  <CourseCard
                    key={course.id}
                    course={course}
                    courseContent={courseContents?.[course.id]}
                    onClick={handleSelectCourse}
                  />
                </Col>
              ) : null;
            })}
          </Row>
        )}
      </Container>
    </PageWrap>
  );
}

/* ─── styled ─── */

const PageWrap = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  overflow-y: auto;
`;

const PageTitle = styled.h2`
  font-size: ${typography.size.h2};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
  margin-bottom: ${spacing[5]};

  ${MOBILE} {
    font-size: ${typography.size.h3};
    margin-bottom: ${spacing[4]};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing[10]} ${spacing[4]};
  color: ${colors.gray500};
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.medium};
`;

export default ElearningStudentCoursePage;
